import React, { useId } from 'react';

/** Google Chat 기본 웹 URL (PC 전용) */
export const GOOGLE_CHAT_URL = 'https://chat.google.com/';

const GOOGLE_CHAT_WINDOW_NAME = 'mrt-google-chat';

function getStoredUserEmail() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return String(u?.email || '').trim().toLowerCase();
  } catch (_) {
    return '';
  }
}

export function getGoogleChatOpenUrl() {
  const email = getStoredUserEmail();
  const domain = email.includes('@') ? email.split('@')[1] : '';

  let chatUrl = GOOGLE_CHAT_URL;
  if (domain && domain !== 'gmail.com' && domain !== 'googlemail.com') {
    chatUrl = `https://chat.google.com/a/${encodeURIComponent(domain)}/`;
  }

  if (!email) return chatUrl;

  return (
    'https://accounts.google.com/AccountChooser?'
    + `Email=${encodeURIComponent(email)}`
    + `&continue=${encodeURIComponent(chatUrl)}`
  );
}

function isMobileLike() {
  const ua = navigator.userAgent || '';
  if (/MrtAppWebView/i.test(ua)) return true;
  if (window.FlutterNativeAuth || window.FlutterNativeQR || window.FlutterNativeGPS) return true;
  if (/Android|iPhone|iPad|iPod/i.test(ua)) return true;
  try {
    if (window.matchMedia('(max-width: 900px)').matches && ('ontouchstart' in window)) {
      return true;
    }
  } catch (_) {
    /* ignore */
  }
  return false;
}

function openHttps(url) {
  try {
    if (typeof window.FlutterNativeOpenUrl?.postMessage === 'function') {
      window.FlutterNativeOpenUrl.postMessage(url);
      return;
    }
  } catch (_) {
    /* ignore */
  }

  try {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  } catch (_) {
    /* ignore */
  }

  try {
    const win = window.open(url, GOOGLE_CHAT_WINDOW_NAME);
    if (win) {
      try {
        win.focus();
      } catch (__) {
        /* ignore */
      }
      return;
    }
  } catch (_) {
    /* ignore */
  }

  window.location.href = url;
}

export function openGoogleChat() {
  // 모바일/앱: 새 창 열지 않고 안내만
  if (isMobileLike()) {
    window.alert('모바일에서는 Google Chat 앱을 통해 실행해 주세요.');
    return;
  }

  openHttps(getGoogleChatOpenUrl());
}

/**
 * 공식 Google Chat 아이콘 (2026 Workspace 스타일)
 * viewBox 192×192 — Wikimedia / workspace.google.com/products/chat
 */
export default function GoogleChatIcon({ size = 24, className = '', title }) {
  const uid = useId().replace(/:/g, '');
  const maskId = `gchatMask-${uid}`;
  const gradId = `gchatGrad-${uid}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 192 192"
      fill="none"
      className={className}
      role={title ? 'img' : 'presentation'}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
    >
      {title ? <title>{title}</title> : null}
      <rect width="160" height="96" x="16" y="28" fill="#00AF57" rx="48" />
      <path
        fill="#0EBC5F"
        d="M133 48c28.167 0 51 22.834 51 51 0 28.167-22.833 51-51 51H96.624l-34.857 23.064c-3.86 2.544-5.789 3.816-7.372 3.92a6 6 0 0 1-5.612-3.022C48 172.583 48 170.271 48 165.649V148.81C25.121 143.78 8 123.39 8 99c0-28.166 22.834-51 51-51h74z"
      />
      <mask
        id={maskId}
        width="176"
        height="129"
        x="8"
        y="48"
        maskUnits="userSpaceOnUse"
        style={{ maskType: 'alpha' }}
      >
        <path
          fill="#0EBC5F"
          d="M133 48c28.167 0 51 22.834 51 51 0 28.167-22.833 51-51 51H96.722l-39.428 25.896c-3.99 2.62-9.294-.242-9.294-5.015V148.81C25.121 143.78 8 123.39 8 99c0-28.166 22.834-51 51-51h74z"
        />
      </mask>
      <g mask={`url(#${maskId})`}>
        <rect width="160" height="96" x="16" y="28" fill="#0EBC5F" rx="48" />
        <rect width="160" height="96" x="16" y="28" fill={`url(#${gradId})`} rx="48" />
        <path
          stroke="#fff"
          strokeLinecap="round"
          strokeWidth="12"
          d="M62 94s8.84 18 34 18 34-17.182 34-17.182"
        />
      </g>
      <defs>
        <linearGradient
          id={gradId}
          x1="96"
          x2="96"
          y1="28"
          y2="124"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset=".09" stopColor="#94D4FF" />
          <stop offset=".28" stopColor="#78C9FF" />
          <stop offset=".88" stopColor="#01AE58" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}
